/**
 * Raise a customer invoice — `/finance/customer-invoices/new`.
 *
 * This creates a DRAFT. The per-branch legal series `INV-{branch}-{FY}-{seq}`
 * is allocated at finalization, so nothing here asks for or shows a number.
 *
 * Tax is never supplied by the caller: each line names its determined
 * `tax_code_id` and the tax engine calculates the amount, which is then
 * snapshotted onto the line so the document can be reprinted years later
 * without re-reading configuration. The form therefore offers a tax CODE per
 * line and no tax amount field at all.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";
import { Plus, Trash2 } from "lucide-react";

import { surfaceProblem } from "@/components/form";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { useCurrentBranch } from "@/lib/branch/branch-store";

import {
  createCustomerInvoice,
  type CreateCustomerInvoiceLine,
  type CreateCustomerInvoiceRequest,
  type CustomerInvoice,
} from "../api";
import {
  AMOUNT_SCALE,
  QUANTITY_SCALE,
  multiplyDecimals,
  normalizeDecimal,
  sumDecimals,
  todayIsoDate,
} from "../decimal";
import {
  DateField,
  DecimalField,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  SelectField,
  TextField,
} from "../components/fields";
import { financeKeys, usePartyOptions, usePostableAccounts, useTaxCodeList } from "../queries";

const NONE = "__none__";

interface LineDraft {
  key: string;
  description: string;
  quantity: string;
  unitPrice: string;
  revenueAccountId: string;
  taxCodeId: string;
}

let lineSeq = 0;
function newLine(): LineDraft {
  lineSeq += 1;
  return {
    key: `ar-line-${lineSeq}`,
    description: "",
    quantity: "",
    unitPrice: "",
    revenueAccountId: NONE,
    taxCodeId: NONE,
  };
}

export interface CustomerInvoiceNewPageProps {
  onCreated: (invoiceId: string) => void;
  onCancel: () => void;
  canReadParties?: boolean;
  canReadAccounts?: boolean;
  canReadTaxCodes?: boolean;
  /**
   * The shipment this invoice bills, when the operator arrived from one. The
   * pair (type, id) is what makes "invoice this shipment exactly once"
   * checkable, so it is cited on the request rather than merely remembered.
   */
  shipmentId?: string;
}

export function CustomerInvoiceNewPage({
  onCreated,
  onCancel,
  canReadParties = true,
  canReadAccounts = true,
  shipmentId,
  canReadTaxCodes = true,
}: CustomerInvoiceNewPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { branchId } = useCurrentBranch();

  const [partyId, setPartyId] = useState(NONE);
  const [currency, setCurrency] = useState("SAR");
  const [fxRate, setFxRate] = useState("1");
  const [issueDate, setIssueDate] = useState(todayIsoDate);
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>(() => [newLine()]);
  const [formError, setFormError] = useState<string | null>(null);

  const partiesQuery = usePartyOptions("customer", canReadParties);
  const accountsQuery = usePostableAccounts(canReadAccounts);
  const taxCodesQuery = useTaxCodeList({ active: true }, 200);

  const partyOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...(partiesQuery.data ?? []).map((party) => ({
        value: party.id,
        label: party.display_name,
      })),
    ],
    [partiesQuery.data, t],
  );

  const revenueOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      // The ledger's account-type vocabulary calls revenue accounts `income`.
      ...(accountsQuery.data ?? [])
        .filter((account) => account.is_postable && account.account_type === "income")
        .map((account) => ({ value: account.id, label: `${account.code} · ${account.name}` })),
    ],
    [accountsQuery.data, t],
  );

  const taxCodeOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...taxCodesQuery.rows.map((code) => ({
        value: code.id,
        label: `${code.code} · ${code.name}`,
      })),
    ],
    [taxCodesQuery.rows, t],
  );

  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));

  /** Net preview only. Tax is the engine's answer, never a client calculation. */
  const netPreview = useMemo(() => {
    const amounts: string[] = [];
    for (const line of lines) {
      const quantity = normalizeDecimal(line.quantity, { scale: QUANTITY_SCALE });
      const unitPrice = normalizeDecimal(line.unitPrice, { scale: AMOUNT_SCALE });
      if (quantity === null || unitPrice === null) continue;
      amounts.push(multiplyDecimals(quantity, unitPrice));
    }
    return sumDecimals(amounts);
  }, [lines]);

  const createMutation = useIdempotentMutation<CustomerInvoice, CreateCustomerInvoiceRequest>({
    mutationFn: (body, key) => createCustomerInvoice(body, key),
    onSuccess: (invoice) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.customerInvoices.all });
      onCreated(invoice.id);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const submit = () => {
    if (branchId === null) {
      setFormError(
        t(
          "finance.common.branchRequired",
          "Select an acting branch before creating an operational document.",
        ),
      );
      return;
    }
    if (partyId === NONE) {
      setFormError(t("finance.customerInvoices.customerRequired", "Choose the customer."));
      return;
    }
    if (dueDate !== "" && dueDate < issueDate) {
      setFormError(
        t("finance.customerInvoices.dueBeforeIssue", "The due date cannot precede the issue date."),
      );
      return;
    }

    const wireLines: CreateCustomerInvoiceLine[] = [];
    for (const [index, line] of lines.entries()) {
      const position = index + 1;
      if (line.description.trim() === "") {
        setFormError(
          t(
            "finance.customerInvoices.lineDescriptionRequired",
            "Line {{line}} needs a description.",
            {
              line: position,
            },
          ),
        );
        return;
      }
      const quantity = normalizeDecimal(line.quantity, {
        scale: QUANTITY_SCALE,
        allowZero: false,
      });
      if (quantity === null) {
        setFormError(
          t(
            "finance.customerInvoices.lineQuantityInvalid",
            "Line {{line}} needs a non-zero quantity.",
            {
              line: position,
            },
          ),
        );
        return;
      }
      const unitPrice = normalizeDecimal(line.unitPrice, {
        scale: AMOUNT_SCALE,
        allowNegative: true,
      });
      if (unitPrice === null) {
        setFormError(
          t("finance.customerInvoices.linePriceInvalid", "Line {{line}} needs a unit price.", {
            line: position,
          }),
        );
        return;
      }
      if (line.revenueAccountId === NONE) {
        setFormError(
          t(
            "finance.customerInvoices.lineRevenueRequired",
            "Line {{line}} needs a revenue account.",
            {
              line: position,
            },
          ),
        );
        return;
      }
      if (line.taxCodeId === NONE) {
        setFormError(
          t(
            "finance.customerInvoices.lineTaxCodeRequired",
            "Line {{line}} needs its determined tax code — the engine calculates the amount from it.",
            { line: position },
          ),
        );
        return;
      }
      wireLines.push({
        description: line.description.trim(),
        quantity,
        unit_price: unitPrice,
        revenue_account_id: line.revenueAccountId,
        tax_code_id: line.taxCodeId,
      });
    }

    const fx = normalizeDecimal(fxRate, { scale: 4, allowZero: false });
    if (fx === null) {
      setFormError(t("finance.common.fxRateInvalid", "Enter a positive exchange rate."));
      return;
    }

    setFormError(null);
    createMutation.mutate({
      branch_id: branchId,
      party_id: partyId,
      currency,
      fx_rate: fx,
      issue_date: issueDate,
      ...(dueDate === "" ? {} : { due_date: dueDate }),
      ...(notes.trim() === "" ? {} : { notes: notes.trim() }),
      // Citing the shipment is what lets the books answer "has this already
      // been invoiced" without anybody remembering.
      ...(shipmentId === undefined
        ? {}
        : { source_document_type: "shipment" as const, source_document_id: shipmentId }),
      lines: wireLines,
    });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.customerInvoices.createTitle", "New customer invoice")}
        description={
          shipmentId === undefined
            ? t(
                "finance.customerInvoices.createSubtitle",
                "Creates a draft. The legal series number is allocated when you finalize it.",
              )
            : t(
                "finance.customerInvoices.createForShipment",
                "Creates a draft that cites the shipment it bills, so the same goods cannot be invoiced twice unnoticed.",
              )
        }
        actions={
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t("actions.cancel")}
          </Button>
        }
      />

      {branchId === null ? (
        <NoticeBanner>
          {t(
            "finance.common.branchRequired",
            "Select an acting branch before creating an operational document.",
          )}
        </NoticeBanner>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.customerInvoices.headerSection", "Invoice")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="ci-party"
            label={t("finance.customerInvoices.customer", "Customer")}
            value={partyId}
            onValueChange={setPartyId}
            options={partyOptions}
            disabled={!canReadParties}
          />
          <TextField
            id="ci-currency"
            label={t("finance.common.currency", "Currency")}
            value={currency}
            onChange={(value) => setCurrency(value.toUpperCase())}
            ltr
            maxLength={3}
          />
          <DecimalField
            id="ci-fx"
            label={t("finance.common.fxRate", "Exchange rate")}
            value={fxRate}
            onChange={setFxRate}
            decimal={{ scale: 4, allowZero: false }}
            hint={t("finance.common.fxRateHint", "Base-currency units per document-currency unit.")}
            invalidMessage={t("finance.common.fxRateInvalid", "Enter a positive exchange rate.")}
          />
          <DateField
            id="ci-issue-date"
            label={t("finance.customerInvoices.issueDate", "Issue date")}
            value={issueDate}
            onChange={setIssueDate}
          />
          <DateField
            id="ci-due-date"
            label={t("finance.customerInvoices.dueDate", "Due date")}
            value={dueDate}
            onChange={setDueDate}
            hint={t(
              "finance.customerInvoices.dueDateHint",
              "Leave empty to derive it from the customer's payment terms.",
            )}
          />
          <TextField
            id="ci-notes"
            label={t("finance.customerInvoices.notes", "Notes")}
            value={notes}
            onChange={setNotes}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3">
          <CardTitle>
            <h2>{t("finance.customerInvoices.linesSection", "Lines")}</h2>
          </CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setLines((current) => [...current, newLine()])}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("finance.customerInvoices.addLine", "Add line")}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          {lines.map((line, index) => (
            <div key={line.key} className="flex flex-col gap-3 border-b pb-4 last:border-b-0">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-medium">
                  {t("finance.customerInvoices.lineHeading", "Line {{line}}", { line: index + 1 })}
                </h3>
                {lines.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t("finance.customerInvoices.removeLine", "Remove line {{line}}", {
                      line: index + 1,
                    })}
                    onClick={() =>
                      setLines((current) =>
                        current.filter((candidate) => candidate.key !== line.key),
                      )
                    }
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <TextField
                  id={`ci-description-${line.key}`}
                  label={t("finance.customerInvoices.lineDescription", "Description")}
                  value={line.description}
                  onChange={(description) => updateLine(line.key, { description })}
                  className="lg:col-span-2"
                />
                <DecimalField
                  id={`ci-quantity-${line.key}`}
                  label={t("finance.customerInvoices.lineQuantity", "Quantity")}
                  value={line.quantity}
                  onChange={(quantity) => updateLine(line.key, { quantity })}
                  decimal={{ scale: QUANTITY_SCALE, allowZero: false }}
                  invalidMessage={t(
                    "finance.customerInvoices.quantityInvalid",
                    "Quantities carry at most six decimals and cannot be zero.",
                  )}
                />
                <DecimalField
                  id={`ci-unit-price-${line.key}`}
                  label={t("finance.customerInvoices.lineUnitPrice", "Unit price")}
                  value={line.unitPrice}
                  onChange={(unitPrice) => updateLine(line.key, { unitPrice })}
                  decimal={{ scale: AMOUNT_SCALE, allowNegative: true }}
                  invalidMessage={t(
                    "finance.customerInvoices.priceInvalid",
                    "Amounts carry at most four decimals.",
                  )}
                />
                <SelectField
                  id={`ci-revenue-${line.key}`}
                  label={t("finance.customerInvoices.lineRevenueAccount", "Revenue account")}
                  value={line.revenueAccountId}
                  onValueChange={(revenueAccountId) => updateLine(line.key, { revenueAccountId })}
                  options={revenueOptions}
                  disabled={!canReadAccounts}
                />
                <SelectField
                  id={`ci-tax-code-${line.key}`}
                  label={t("finance.customerInvoices.lineTaxCode", "Tax code")}
                  value={line.taxCodeId}
                  onValueChange={(taxCodeId) => updateLine(line.key, { taxCodeId })}
                  options={taxCodeOptions}
                  disabled={!canReadTaxCodes}
                  hint={t(
                    "finance.customerInvoices.taxCodeHint",
                    "The engine calculates the amount from this code and snapshots it.",
                  )}
                  className="lg:col-span-2"
                />
              </div>
            </div>
          ))}

          <p className="text-sm text-muted-foreground" data-testid="customer-invoice-net-preview">
            {t(
              "finance.customerInvoices.netPreview",
              "Net of entered lines: {{amount}} {{currency}}",
              {
                amount: netPreview,
                currency,
              },
            )}
          </p>

          {formError === null ? null : <ProblemBanner>{formError}</ProblemBanner>}

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" loading={createMutation.isPending} onClick={submit}>
              {t("finance.customerInvoices.createDraft", "Create draft invoice")}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t("actions.cancel")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
